import {
  type FlowNodeData,
  type FlowRunSummary,
  type Strategy,
  type DrawerTab,
} from "@/features/office/types/flow.types";
import type { AgentKey } from "@/features/office/config/agents.config";
import type { HITLRequest, PipelinePhase } from "@/types/agents";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { shouldUseMockData } from "@/lib/mock-data/utils";
import { flowAPIService } from "../services/flow-api.service";

type LiveControl = {
  stop: () => void;
  resumeFromHITL: (approved: boolean) => void;
  isPausedAtHITL: () => boolean;
};
import {
  getRunTranscript,
  TranscriptForbiddenError,
} from "../services/run-contract.service";
import { transcriptEventsToMessages } from "../utils/agent-stream";
import { flowNodesEqual } from "../utils/node-equality";
import {
  spectateBackendLive,
  startBackendLive,
} from "../hooks/use-backend-live";
import type { AgentEvent } from "@/lib/mock-data/types";
import type { AutomationLevel } from "@/features/trading-preferences/trading-preferences.service";

// View mode for the flow page
export type FlowViewMode = "linear" | "dag";

export type LiveRunConnectionStatus =
  | "idle"
  | "starting"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "error";

// Distinguishes a genuinely-empty desk (no runs yet) from a desk whose
// last-run hydration FAILED (fetch error) — the two must not look identical.
export type HydrationStatus =
  | "idle" // not attempted yet
  | "loading" // fetch in flight
  | "loaded" // last run hydrated
  | "empty" // fetch succeeded, backend has no runs
  | "error"; // fetch failed — show a retry affordance

// SIG-289: the transcript replay has its own honesty ladder — a 403
// (someone else's run / no access) must NOT look like an empty feed.
export type TranscriptStatus =
  | "idle" // not attempted yet
  | "ok" // events replayed into Agent Activity
  | "empty" // fetch succeeded, run has no persisted events — office floor must say so
  | "unavailable"; // HTTP 403 — show "Transcript unavailable for this run"

// Agent message type for chat accumulation
export interface AccumulatedMessage {
  id: string;
  agent: string;
  agentAvatar: string;
  content: string;
  timestamp: Date;
  type: "message" | "handoff" | "decision" | "chart";
  metadata?: {
    toAgent?: string;
    confidence?: number;
    score?: number;
  };
}

interface FlowStore {
  // View mode toggle
  viewMode: FlowViewMode;
  setViewMode: (mode: FlowViewMode) => void;

  isLive: boolean;
  setIsLive: (isLive: boolean) => void;

  showHistory: boolean;
  setShowHistory: (showHistory: boolean) => void;

  history: FlowRunSummary[];
  loadHistory: () => Promise<void>;

  selectedRunId: string | null;
  setSelectedRunId: (id: string | null) => void;
  clearRunSelection: () => void;
  currentNodes: FlowNodeData[];
  loadRun: (id: string) => Promise<void>;
  /**
   * Hydrate the desk with the most recent run's final state from the backend
   * (real last-known agent state from the DB) when no live run is active.
   * No-ops in mock mode, during a live run, or when nodes are already loaded.
   */
  hydrateLatestRun: () => Promise<void>;
  /** Distinguishes a truly-empty desk from a failed history/snapshot fetch. */
  hydrationStatus: HydrationStatus;
  /** SIG-289: forbidden (403) transcript vs genuinely-empty transcript. */
  transcriptStatus: TranscriptStatus;
  /**
   * Monotonic marker bumped on any live-run activity (run start + each live
   * node update). hydrateLatestRun captures it and aborts its `set` if it
   * changed during the awaits — so a live run that arrives mid-fetch is never
   * clobbered by stale hydrated state.
   */
  liveActivityEpoch: number;
  setCurrentNode: (id: string) => Promise<void>;
  currentRunInfo?: { id: string; timestamp?: string; duration?: number } | null;

  // Accumulated message history for Agent Chat (persists through live run)
  messageHistory: AccumulatedMessage[];
  addMessage: (message: AccumulatedMessage) => void;
  clearMessageHistory: () => void;

  isPlaying: boolean;
  isPaused: boolean;
  play: () => Promise<void>;
  pause: () => void;
  replay: () => Promise<void>;
  replayArmed?: boolean;
  // live orchestration
  liveActive?: boolean;
  liveRunId?: string | null;
  liveConnectionStatus: LiveRunConnectionStatus;
  liveError: string | null;
  enableLive: () => void;
  /** Attach to a run the autonomous pipeline already started (honest idle when none). */
  enableSpectate: () => void;
  _startLiveSession: (mode: "start" | "spectate") => void;
  disableLive: () => void;
  liveControl?: LiveControl;

  // DAG-specific state
  currentPhase: PipelinePhase | null;
  setCurrentPhase: (phase: PipelinePhase | null) => void;
  executionPath: "api" | "browser" | null;
  setExecutionPath: (path: "api" | "browser" | null) => void;

  // HITL state
  pendingHITL: HITLRequest | null;
  setPendingHITL: (request: HITLRequest | null) => void;
  clearPendingHITL: (requestId: string) => void;
  showHITLModal: boolean;
  setShowHITLModal: (show: boolean) => void;
  hitlSubmissionError: string | null;
  /** Handle HITL decision in demo mode - resumes flow */
  handleDemoHITLDecision: (approved: boolean) => void;
  /** Manually trigger HITL demo modal (for clicking HITL node) */
  triggerDemoHITL: () => void;
  /** Whether flow is currently paused at HITL */
  isPausedAtHITL: boolean;

  // recent activity filters
  activityBroker: "all" | "tradovate";
  activityStrategy: "all" | Strategy; // single-select
  setActivityBroker: (b: "all" | "tradovate") => void;
  setActivityStrategy: (s: "all" | Strategy) => void;
  clearActivityFilters: () => void;

  // Automation mode (PRD-14.5)
  automationMode: AutomationLevel | null;
  setAutomationMode: (mode: AutomationLevel | null) => void;

  // Wave 2: Live start inputs (symbol / optional strategy registry id)
  runSymbol: string;
  runStrategyId: number | null;
  runTradingGroupId: number | null;
  setRunSymbol: (symbol: string) => void;
  setRunStrategyId: (strategyId: number | null) => void;
  setRunTradingGroupId: (groupId: number | null) => void;

  // SV-20: Drawer state for agent detail panel
  selectedAgentKey: AgentKey | null;
  drawerOpen: boolean;
  drawerTab: DrawerTab;
  drawerPinned: boolean;
  setSelectedAgentKey: (key: AgentKey | null) => void;
  openDrawer: (key: AgentKey, tab?: DrawerTab) => void;
  closeDrawer: () => void;
  setDrawerTab: (tab: DrawerTab) => void;
  toggleDrawerPin: () => void;
}

function createOfficeErrorNode(error: unknown): FlowNodeData[] {
  const message =
    error instanceof Error ? error.message : "Backend live execution failed";

  return [
    {
      id: "backend-live-error",
      name: "Backend Live",
      duration: 0,
      status: "error",
      current: true,
      message,
      data: [{ label: "Error", value: message }],
    },
  ];
}

let runSelectionGeneration = 0;
let liveSessionGeneration = 0;

export const useFlowStore = create<FlowStore>()(
  persist<FlowStore>(
    (set, get) => ({
      // View mode - default to DAG for new flow page
      viewMode: "dag",
      setViewMode: (mode) => set({ viewMode: mode }),

      isLive: false,
      setIsLive: (isLive: boolean) => set({ isLive }),

      showHistory: true, // Default to true so Flow Activity is always visible
      setShowHistory: (showHistory: boolean) => set({ showHistory }),

      history: [],
      loadHistory: async () => {
        if (shouldUseMockData()) armSimBridge();
        const list = shouldUseMockData()
          ? await (await import("../mock/api")).fetchFlowHistory()
          : await flowAPIService.getFlowHistory();
        set({ history: list });
      },

      selectedRunId: null,
      setSelectedRunId: (id) => set({ selectedRunId: id }),
      clearRunSelection: () => {
        runSelectionGeneration += 1;
        set({
          selectedRunId: null,
          currentNodes: [],
          currentRunInfo: null,
          messageHistory: [],
          hydrationStatus: "idle",
          transcriptStatus: "idle",
          replayArmed: false,
        });
      },
      currentNodes: [],
      currentRunInfo: null,

      // Message history for Agent Chat accumulation
      messageHistory: [],
      addMessage: (message) =>
        set((state) =>
          state.messageHistory.some((existing) => existing.id === message.id)
            ? {}
            : { messageHistory: [...state.messageHistory, message] },
        ),
      clearMessageHistory: () => set({ messageHistory: [] }),
      loadRun: async (id: string) => {
        const selectionGeneration = ++runSelectionGeneration;
        liveSessionGeneration += 1;
        get().liveControl?.stop();
        const isMock = shouldUseMockData();
        if (isMock) armSimBridge();
        set({
          selectedRunId: id,
          hydrationStatus: "loading",
          liveControl: undefined,
          liveActive: false,
          liveRunId: null,
          liveConnectionStatus: "idle",
          liveError: null,
          isLive: false,
          isPlaying: false,
          isPaused: false,
          isPausedAtHITL: false,
          pendingHITL: null,
          showHITLModal: false,
          hitlSubmissionError: null,
          replayArmed: false,
        });

        const transcriptPromise = isMock
          ? Promise.resolve({
              messages: [] as AccumulatedMessage[],
              status: "idle" as TranscriptStatus,
            })
          : getRunTranscript(id)
              .then((events) => {
                if (events === null) {
                  return {
                    messages: [] as AccumulatedMessage[],
                    status: "idle" as TranscriptStatus,
                  };
                }
                const messages = transcriptEventsToMessages(events, id);
                return {
                  messages,
                  status: (messages.length > 0
                    ? "ok"
                    : "empty") as TranscriptStatus,
                };
              })
              .catch((error: unknown) => {
                if (error instanceof TranscriptForbiddenError) {
                  return {
                    messages: [] as AccumulatedMessage[],
                    status: "unavailable" as TranscriptStatus,
                  };
                }
                throw error;
              });
        const [run, transcript] = await Promise.all([
          isMock
            ? (await import("../mock/api")).fetchFlowRunById(id)
            : flowAPIService.getFlowRun(id),
          transcriptPromise,
        ]).catch((error: unknown) => {
          if (
            selectionGeneration === runSelectionGeneration &&
            get().selectedRunId === id
          ) {
            set({ hydrationStatus: "error" });
          }
          throw error;
        });
        if (
          selectionGeneration !== runSelectionGeneration ||
          get().selectedRunId !== id
        ) {
          return;
        }
        if (!run) {
          set({ hydrationStatus: "error" });
          return;
        }
        set({
          currentNodes: run.nodes,
          isLive: false,
          messageHistory: transcript.messages,
          transcriptStatus: transcript.status,
          hydrationStatus: "loaded",
          currentRunInfo: {
            id: run.id,
            timestamp: run.timestamp,
            duration: run.duration,
          },
        });
      },

      hydrateLatestRun: async () => {
        const { liveActive, currentNodes, selectedRunId } = get();
        if (
          shouldUseMockData() ||
          liveActive ||
          currentNodes.length > 0 ||
          selectedRunId
        ) {
          return;
        }
        // Capture the live-activity marker; if it changes during the awaits, a
        // live run arrived and we must NOT clobber it with hydrated state.
        // (enableLive + each live onUpdate bump the epoch, so this fully covers a
        // live run arriving mid-fetch — without falsely tripping on the nodes THIS
        // function sets, which is why we don't re-check currentNodes here.)
        const startEpoch = get().liveActivityEpoch;
        const selectionGeneration = runSelectionGeneration;
        const liveArrived = () =>
          get().liveActivityEpoch !== startEpoch || !!get().liveActive;
        const hydrationInvalidated = () =>
          liveArrived() || selectionGeneration !== runSelectionGeneration;

        set({ hydrationStatus: "loading" });
        try {
          const history = get().history.length
            ? get().history
            : await flowAPIService.getFlowHistory();
          if (hydrationInvalidated()) return;
          const latest = history[0];
          if (!latest) {
            set({ hydrationStatus: "empty" });
            return;
          }
          const run = await flowAPIService.getFlowRun(latest.id);
          if (hydrationInvalidated()) return;
          if (!run) {
            set({ hydrationStatus: "empty" });
            return;
          }
          // Re-check the guard immediately before applying — a live run may have
          // started (and even set nodes) while we were fetching.
          set({
            // Final statuses as persisted — last-known state, NOT a replay reset.
            currentNodes: run.nodes,
            selectedRunId: run.id,
            isLive: false,
            hydrationStatus: "loaded",
            currentRunInfo: {
              id: run.id,
              timestamp: run.timestamp,
              duration: run.duration,
            },
          });

          // Progressive enhancement: replay the persisted transcript into Agent
          // Chat. SIG-289 (PR #519 r2): the replay result drives transcriptStatus
          // on the hydration path too — 403 → "unavailable", a real empty
          // transcript → "empty", replayed events → "ok". A non-403 fetch failure
          // returns null and leaves transcriptStatus "idle": we don't know whether
          // events exist, so we claim neither empty-success nor unavailability
          // (the desk itself still hydrated — only the transcript is unknown).
          if (get().messageHistory.length === 0) {
            try {
              const events = await getRunTranscript(run.id);
              // A live run may have arrived during the transcript fetch too.
              if (hydrationInvalidated()) return;
              if (events !== null) {
                const messages = transcriptEventsToMessages(events, run.id);
                if (messages.length > 0 && get().messageHistory.length === 0) {
                  set({ messageHistory: messages, transcriptStatus: "ok" });
                } else {
                  set({ transcriptStatus: "empty" });
                }
              }
            } catch (transcriptError) {
              if (transcriptError instanceof TranscriptForbiddenError) {
                if (!hydrationInvalidated()) {
                  set({ transcriptStatus: "unavailable" });
                }
              } else {
                throw transcriptError;
              }
            }
          }
        } catch (error) {
          // Fetch FAILED — distinct from a genuinely-empty desk. The UI shows a
          // "couldn't load desk history" retry state, not the empty state.
          console.error("[FlowStore] Failed to hydrate latest run:", error);
          if (!hydrationInvalidated()) set({ hydrationStatus: "error" });
        }
      },
      hydrationStatus: "idle",
      transcriptStatus: "idle",
      liveActivityEpoch: 0,

      setCurrentNode: async (id: string) => {
        const runId = get().selectedRunId;
        if (!runId) return;
        const run = shouldUseMockData()
          ? await (await import("../mock/api")).fetchFlowRunById(runId)
          : await flowAPIService.getFlowRun(runId);
        if (!run) return;
        const targetIndex = run.nodes.findIndex((n) => n.id === id);
        if (targetIndex === -1) return;
        set({
          currentNodes: run.nodes.map((_node, idx) => {
            if (idx < targetIndex) return { ...run.nodes[idx], current: false };
            if (idx === targetIndex)
              return { ...run.nodes[idx], current: true };
            return { ...run.nodes[idx], status: "waiting", current: false };
          }),
        });
      },

      isPlaying: false,
      isPaused: false,
      replayArmed: false,
      liveActive: false,
      liveRunId: null,
      liveConnectionStatus: "idle",
      liveError: null,
      play: async () => {
        const { selectedRunId } = get();
        if (!selectedRunId) return;
        const run = shouldUseMockData()
          ? await (await import("../mock/api")).fetchFlowRunById(selectedRunId)
          : await flowAPIService.getFlowRun(selectedRunId);
        if (!run) return;
        const currentIdx = get().currentNodes.findIndex((n) => n.current);
        const startIndex = get().replayArmed
          ? 0
          : get().isPaused && currentIdx >= 0
            ? currentIdx + 1
            : 0;
        set({ isPlaying: true, isPaused: false, replayArmed: false });
        for (let i = startIndex; i < run.nodes.length; i++) {
          if (i > startIndex) {
            await new Promise((res) =>
              setTimeout(res, run.nodes[i - 1].duration),
            );
          }
          if (!get().isPlaying) break;
          set(() => ({
            currentNodes: run.nodes.map((_node, idx) => {
              if (idx < i) return { ...run.nodes[idx], current: false };
              if (idx === i) return { ...run.nodes[idx], current: true };
              return { ...run.nodes[idx], status: "waiting", current: false };
            }),
          }));
          if (run.nodes[i].status === "error") break;
        }
        set({ isPlaying: false });
      },

      // DAG-specific state
      currentPhase: null,
      setCurrentPhase: (phase) => set({ currentPhase: phase }),
      executionPath: null,
      setExecutionPath: (path) => set({ executionPath: path }),

      // HITL state
      pendingHITL: null,
      setPendingHITL: (request) =>
        set({
          pendingHITL: request,
          ...(request ? { hitlSubmissionError: null } : {}),
        }),
      clearPendingHITL: (requestId) =>
        set((state) => {
          if (state.pendingHITL?.id !== requestId) return {};
          return {
            pendingHITL: null,
            showHITLModal: false,
            isPausedAtHITL: false,
            hitlSubmissionError: null,
          };
        }),
      showHITLModal: false,
      setShowHITLModal: (show) => set({ showHITLModal: show }),
      hitlSubmissionError: null,
      isPausedAtHITL: false,

      // Handle HITL decision in demo mode
      handleDemoHITLDecision: (approved: boolean) => {
        const { liveControl } = get();
        if (liveControl) {
          liveControl.resumeFromHITL(approved);
        }
        set({
          showHITLModal: false,
          pendingHITL: null,
          isPausedAtHITL: false,
          hitlSubmissionError: null,
        });
      },

      // Manually trigger HITL demo modal
      triggerDemoHITL: () => {
        void import("../mock/flow.mock").then(({ createMockHITLRequest }) => {
          set({
            pendingHITL: createMockHITLRequest(),
            showHITLModal: true,
            isPausedAtHITL: true,
            hitlSubmissionError: null,
          });
        });
      },

      // recent activity filters
      activityBroker: "all",
      activityStrategy: "all",
      setActivityBroker: (b) => set({ activityBroker: b }),
      setActivityStrategy: (s) => set({ activityStrategy: s }),
      clearActivityFilters: () =>
        set({ activityBroker: "all", activityStrategy: "all" }),

      automationMode: null,
      setAutomationMode: (mode) => set({ automationMode: mode }),
      runSymbol: "ES",
      runStrategyId: null,
      runTradingGroupId: null,
      setRunSymbol: (symbol) =>
        set({
          runSymbol: (symbol || "ES").trim().toUpperCase() || "ES",
        }),
      setRunStrategyId: (strategyId) => set({ runStrategyId: strategyId }),
      setRunTradingGroupId: (groupId) => set({ runTradingGroupId: groupId }),

      // SV-20: Agent drawer state
      selectedAgentKey: null,
      drawerOpen: false,
      drawerTab: "thoughts",
      drawerPinned: false,

      setSelectedAgentKey: (key) => set({ selectedAgentKey: key }),

      openDrawer: (key, tab) =>
        set({
          selectedAgentKey: key,
          drawerOpen: true,
          ...(tab ? { drawerTab: tab } : {}),
        }),

      closeDrawer: () => {
        // If pinned, don't close -- only swap agent
        if (get().drawerPinned) return;
        set({
          drawerOpen: false,
          // Keep selectedAgentKey so it persists for re-open
        });
      },

      setDrawerTab: (tab) => set({ drawerTab: tab }),

      toggleDrawerPin: () => set((s) => ({ drawerPinned: !s.drawerPinned })),

      pause: () => {
        set({ isPlaying: false, isPaused: true });
      },

      replay: async () => {
        const { selectedRunId, loadRun } = get();
        if (!selectedRunId) return;
        await loadRun(selectedRunId);
        if (get().selectedRunId !== selectedRunId) return;
        set({ isPlaying: false, isPaused: true, replayArmed: true });
      },

      enableLive: () => {
        const { liveActive, isLive, liveConnectionStatus } = get();
        // SIG-563 FE: serialize concurrent Live clicks while a start is in flight.
        if (
          liveActive ||
          isLive ||
          liveConnectionStatus === "starting" ||
          liveConnectionStatus === "reconnecting"
        ) {
          return;
        }
        get()._startLiveSession("start");
      },

      enableSpectate: () => {
        const { liveActive, isLive } = get();
        if (liveActive || isLive || shouldUseMockData()) return;
        get()._startLiveSession("spectate");
      },

      _startLiveSession: (mode) => {
        console.log(`[FlowStore] live session requested (${mode})`);
        const {
          automationMode,
          liveControl: existingControl,
          liveConnectionStatus,
          runSymbol,
          runStrategyId,
          runTradingGroupId,
        } = get();
        const liveAutomationMode = automationMode ?? undefined;
        if (mode === "start" && !liveAutomationMode) {
          set({
            liveConnectionStatus: "error",
            liveError: "Automation preference unavailable",
          });
          return;
        }
        if (
          mode === "start" &&
          (liveConnectionStatus === "starting" || existingControl)
        ) {
          return;
        }
        if (existingControl) existingControl.stop();
        const sessionGeneration = ++liveSessionGeneration;
        const isCurrentSession = () =>
          sessionGeneration === liveSessionGeneration;
        let presentationStarted = false;
        const enterLivePresentation = () => {
          if (presentationStarted || !isCurrentSession()) return;
          presentationStarted = true;
          set((state) => ({
            isLive: true,
            liveActive: true,
            isPlaying: true,
            isPaused: false,
            selectedRunId: null,
            currentNodes: [],
            currentRunInfo: null,
            liveRunId: null,
            liveConnectionStatus: "starting",
            liveError: null,
            messageHistory: [],
            transcriptStatus: "idle",
            liveActivityEpoch: state.liveActivityEpoch + 1,
          }));
        };

        const finishLiveSession = (error?: Error) => {
          if (sessionGeneration !== liveSessionGeneration) return;
          liveSessionGeneration += 1;
          get().liveControl?.stop();
          set({
            liveControl: undefined,
            liveActive: false,
            liveRunId: null,
            liveConnectionStatus: error ? "error" : "idle",
            liveError: error?.message ?? null,
            isPlaying: false,
            isPaused: false,
            isLive: false,
            isPausedAtHITL: false,
            pendingHITL: null,
            showHITLModal: false,
            hitlSubmissionError: null,
          });
          void get()
            .loadHistory()
            .catch((historyError) =>
              console.error(
                "[FlowStore] Failed to refresh terminal run history:",
                historyError,
              ),
            );
        };

        const callbacks = {
          onRunStart: (info: {
            id: string;
            timestamp: string;
            duration: number;
          }) => {
            if (!isCurrentSession()) return;
            enterLivePresentation();
            set({
              currentRunInfo: {
                id: info.id,
                timestamp: info.timestamp,
                duration: info.duration,
              },
              liveRunId: info.id,
            });
          },
          onUpdate: (nodes: FlowNodeData[]) => {
            if (!isCurrentSession()) return;
            // Preserve the previous reference when the snapshot is
            // content-equal — each WS message rebuilds the array, and a
            // fresh identity for identical content forces every store
            // subscriber to re-render per message (the "Maximum update
            // depth exceeded" loop class). Skip set entirely when nothing
            // changed so we don't bump liveActivityEpoch on noise.
            const prev = get().currentNodes;
            if (flowNodesEqual(prev, nodes)) return;
            set((s) => ({
              currentNodes: nodes,
              liveActivityEpoch: s.liveActivityEpoch + 1,
            }));
          },
          onRunEnd: () => finishLiveSession(),
          onHITLRequired: (request: HITLRequest) => {
            if (!isCurrentSession()) return;
            // Auto-trigger HITL modal when flow reaches HITL node
            set({
              pendingHITL: request,
              showHITLModal: true,
              isPausedAtHITL: true,
              hitlSubmissionError: null,
            });
          },
          onHITLCleared: (requestId: string) => {
            if (!isCurrentSession()) return;
            get().clearPendingHITL(requestId);
          },
          onHITLSubmissionError: (requestId: string, error: Error) => {
            if (!isCurrentSession()) return;
            if (get().pendingHITL?.id !== requestId) return;
            set({ hitlSubmissionError: error.message });
          },
          onError: (error: Error) => {
            if (!isCurrentSession()) return;
            set({
              currentNodes: createOfficeErrorNode(error),
            });
            finishLiveSession(error);
          },
          onConnectionStatusChange: (
            status: LiveRunConnectionStatus,
            error?: string | null,
          ) => {
            if (!isCurrentSession()) return;
            set({
              liveConnectionStatus: status,
              liveError:
                error ??
                (status === "error" ? "Backend live execution failed" : null),
            });
          },
          onTranscriptEvent: (message: AccumulatedMessage) => {
            if (!isCurrentSession()) return;
            get().addMessage(message);
            // A desk actively receiving agent activity has a readable transcript
            // by definition — never leave it reading "unavailable"/"empty".
            set({ transcriptStatus: "ok" });
          },
        };

        if (shouldUseMockData() && mode === "start") {
          armSimBridge();
          enterLivePresentation();
          void import("../mock/live")
            .then(({ startLive }) => {
              if (!isCurrentSession()) return;
              const control = startLive(callbacks, {
                automationMode: liveAutomationMode,
              });
              if (!isCurrentSession()) {
                control.stop();
                return;
              }
              set({
                isLive: true,
                liveActive: true,
                isPlaying: true,
                isPaused: false,
                selectedRunId: null,
                liveControl: control,
                liveRunId: null,
                liveConnectionStatus: "connected",
                liveError: null,
                messageHistory: [],
                transcriptStatus: "idle",
              });
            })
            .catch((error: unknown) => {
              if (!isCurrentSession()) return;
              finishLiveSession(
                error instanceof Error
                  ? error
                  : new Error("Failed to start demo live session"),
              );
            });
        } else {
          if (mode === "start") enterLivePresentation();
          const starter =
            mode === "spectate"
              ? spectateBackendLive(callbacks)
              : startBackendLive(callbacks, {
                  automationMode: liveAutomationMode,
                  symbol: runSymbol,
                  strategyId: runStrategyId,
                  tradingGroupId: runTradingGroupId,
                });
          starter
            .then((control) => {
              if (!isCurrentSession()) {
                control?.stop();
                return;
              }
              if (control) {
                enterLivePresentation();
                if (!get().liveActive) {
                  control.stop();
                  return;
                }
                set({ liveControl: control });
                return;
              }
              // Spectate found no active run: the desk is honestly idle.
              set({
                isLive: false,
                liveActive: false,
                isPlaying: false,
                isPaused: false,
                liveRunId: null,
                liveConnectionStatus: "idle",
                liveError: null,
              });
            })
            .catch((err) => {
              if (!isCurrentSession()) return;
              console.error("[FlowStore] Backend live failed:", err);
              const error =
                err instanceof Error
                  ? err
                  : new Error("Backend live execution failed");
              if (mode === "spectate" && !presentationStarted) {
                set({
                  liveConnectionStatus: "error",
                  liveError: error.message,
                });
                return;
              }
              set({ currentNodes: createOfficeErrorNode(error) });
              finishLiveSession(error);
            });
        }
      },

      disableLive: () => {
        liveSessionGeneration += 1;
        const { liveControl } = get();
        if (liveControl) liveControl.stop();
        set({
          liveControl: undefined,
          liveActive: false,
          liveRunId: null,
          liveConnectionStatus: "idle",
          liveError: null,
          isPlaying: false,
          isPaused: false,
          isLive: false,
          isPausedAtHITL: false,
          pendingHITL: null,
          showHITLModal: false,
          hitlSubmissionError: null,
        });
      },
    }),
    {
      name: "office-flow-drawer",
      partialize: (state) =>
        ({
          selectedAgentKey: state.selectedAgentKey,
          drawerTab: state.drawerTab,
          drawerPinned: state.drawerPinned,
        }) as Partial<FlowStore> as FlowStore,
    },
  ),
);

// =============================================================================
// Simulation Store -> Flow Store Bridge
//
// Subscribes to useSimulationStore.agentEvents and converts new AgentEvent
// entries into AccumulatedMessage format for the Agent Chat panel.
// This runs at module load time (client-side only) and does not need cleanup
// since both stores live for the lifetime of the app.
// =============================================================================

/** Map agent names to avatar initials for the chat panel */
function getAgentAvatar(agentName: string): string {
  const avatarMap: Record<string, string> = {
    "Execution Agent": "EA",
    "Risk Agent": "RA",
    "Strategy Agent": "SA",
    "Compliance Agent": "CA",
    "Signal Filter": "SF",
    "Synthesis Agent": "SY",
    User: "U",
    // Pipeline agent personas
    "Carter Hayes": "CH",
    "Ben Kim": "BK",
    Annalisa: "AN",
    "Dollar Bill": "DB",
    Wags: "WG",
    Wendy: "WR",
    Sigma: "SG",
    Lion: "LN",
    "Devin Matrix": "DM",
    Axe: "AX",
  };
  return avatarMap[agentName] ?? agentName.slice(0, 2).toUpperCase();
}

/** Map AgentEvent.action to AccumulatedMessage.type */
function mapActionToMessageType(action: string): AccumulatedMessage["type"] {
  switch (action) {
    case "BUY":
    case "SELL":
    case "CLOSE":
      return "decision";
    case "HANDOFF":
      return "handoff";
    default:
      return "message";
  }
}

let simBridgeArmed = false;

function armSimBridge() {
  if (simBridgeArmed || typeof window === "undefined") return;
  simBridgeArmed = true;
  void import("@/lib/mock-data/simulation-store").then((simulationMod) => {
      let lastEventCount = 0;

      simulationMod.useSimulationStore.subscribe((state) => {
        const currentEvents = state.agentEvents;

        if (currentEvents.length < lastEventCount) {
          lastEventCount = currentEvents.length;
          return;
        }

        if (currentEvents.length > lastEventCount) {
          const newEvents = currentEvents.slice(lastEventCount);
          const flowStore = useFlowStore.getState();

          for (const evt of newEvents) {
            flowStore.addMessage(agentEventToMessage(evt));
          }

          lastEventCount = currentEvents.length;
        }
      });
  });
}

/** Convert an AgentEvent to an AccumulatedMessage */
function agentEventToMessage(evt: AgentEvent): AccumulatedMessage {
  return {
    id: evt.id,
    agent: evt.agentName,
    agentAvatar: getAgentAvatar(evt.agentName),
    content: evt.message,
    timestamp: new Date(evt.timestamp),
    type: mapActionToMessageType(evt.action),
    metadata: evt.relatedPositionId
      ? { confidence: undefined, score: undefined }
      : undefined,
  };
}

